import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { LocalStorage } from 'angular-web-storage';

import { AccountService } from '../../../../../../services/account/account.service';
import { EventTypeService } from '../../../../../../services/event-type/event-type.service';

import {
  EventType,
  EventTypeCategory,
  EventTypeFilters,
  getScheduleTypeDisplay,
  loadEventTypeFiltersFromGetParams
} from '../../../../../../models/event-type';
import { ListResponse } from '../../../../../../models/list-response';
import { SimpleMessageModalComponent } from '../../../../../../components/modals/simple-message-modal/simple-message-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ScheduledEventService } from '../../../../../../services/scheduled-event/scheduled-event.service';
import { addFiltersToGetParams, dateStringFromISO8601, propExists, timeStringFromISO8601 } from '../../../../../../utils';
import { OrganizationConfigService } from '../../../../../../services/organization-config/organization-config.service';
import { EventTypeStatus, ScheduleType } from '../../../../../../models/event-type-enums';
import { Subject } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { DuplicateEventTypeModalComponent } from './modals/duplicate-event-type-modal/duplicate-event-type-modal.component';
import { LocationService } from '../../../../../../services/location/location.service';

@Component({
  selector: 'app-event-types-list',
  templateUrl: './event-types-list.component.html',
  styleUrls: ['./event-types-list.component.css']
})
export class EventTypesListComponent implements OnInit {

  public pageNumber = 1;
  @LocalStorage() public pageSize;
  public filters: EventTypeFilters = {};
  public showAllFilters = false;

  public filtersChangedSubject = new Subject<null>();

  public eventTypeList: ListResponse<EventType>;

  public categories: EventTypeCategory[];

  public loadingEventTypes = true;
  public loadingCategories = true;
  public loadingSave = false;

  public successMessage = '';
  public errorMessage = '';

  public serverError: number = null;

  // Make browser history available to the template.
  public history: History;

  public readonly EventTypeStatus = EventTypeStatus;
  public readonly ScheduleType = ScheduleType;
  public readonly getScheduleTypeDisplay = getScheduleTypeDisplay;

  // The number of event type list requests. Used to ensure that only the most
  // recent request is displayed, in the event of concurrent requests (due to,
  // e.g., filtering).
  private eventTypeListRequestCount = 0;

  constructor(
    public accountService: AccountService,
    public locationService: LocationService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private eventTypeService: EventTypeService,
    private scheduledEventService: ScheduledEventService,
    private orgService: OrganizationConfigService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    if (!this.accountService.getMyAccount().can_manage_events) {
      return;
    }

    if (!this.pageSize) {
      this.pageSize = 10;
    }
    // This is needed in order for the pageSize to be updated when the html
    // select is first changed.
    // noinspection SillyAssignmentJS
    this.pageSize = this.pageSize;

    // Make browser history available to the template.
    this.history = history;

    this.filtersChangedSubject.pipe(
      map(() => { this.loadingEventTypes = true; }),
      debounceTime(300)
    ).subscribe(() => {
      this.loadEventTypes();
      addFiltersToGetParams(
        this.filters,
        this.router,
        this.activatedRoute
      );
    });

    // Load initial query parameters from URL if present.
    this.filters = loadEventTypeFiltersFromGetParams(this.activatedRoute);
    if (!propExists(this.filters.status)) {
      this.filters.status = EventTypeStatus.active;
    }
    const hasHiddenFilters = (
      this.filters.event_category_id ||
      this.filters.location_id ||
      this.filters.schedule_type
    );
    if (hasHiddenFilters) { this.showAllFilters = true; }

    this.loadEventTypes();
    this.loadCategories();
  }

  /**
   * Load the page of event types according to the current page and filter
   * selections.
   */
  public loadEventTypes() {
    this.loadingEventTypes = true;

    // Increment the request count and save a local copy.
    this.eventTypeListRequestCount++;
    const thisRequestCount = this.eventTypeListRequestCount;

    this.eventTypeService
      .getEventTypes(this.pageNumber, this.pageSize, this.filters)
      .then(response => {
        // Only update the event type list if there has not been a more recent
        // request initiated.
        if (thisRequestCount === this.eventTypeListRequestCount) {
          this.eventTypeList = response;
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        if (thisRequestCount === this.eventTypeListRequestCount) {
          this.loadingEventTypes = false;
        }
      });
  }

  private loadCategories() {
    this.loadingCategories = true;

    this.eventTypeService
      .getEventTypeCategories(1, 3000)
      .then(response => {
        this.categories = response.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingCategories = false; })
  }

  /**
   * Update the current page number to the given value and load the page of
   * event types.
   *
   * @param newPageNumber - The new page number.
   */
  public setPage(newPageNumber: number) {
    this.pageNumber = newPageNumber;
    this.loadEventTypes();
  }

  /**
   * Get the number of the last page of results.
   */
  public lastPageNumber() {
    if (!this.eventTypeList) { return 1; }
    return Math.ceil(this.eventTypeList.count / this.pageSize);
  }

  public activateEventType(eventType: EventType) {
    this.successMessage = '';
    this.errorMessage = '';

    const modalRef = this.modalService.open(SimpleMessageModalComponent);
    modalRef.componentInstance.headerText = 'Activate Event';
    modalRef.componentInstance.messageText = 'Are you sure you want to activate this event? Once active, this event will be available for booking, and the event may not be reverted to Draft.';
    modalRef.componentInstance.messageAlertClass = 'alert-info';
    modalRef.componentInstance.hasConfirmation = true;
    modalRef.componentInstance.confirmedCallback = () => {
      this.loadingSave = true;

      const eventTypeCopy: EventType = {...eventType};
      eventTypeCopy.status = EventTypeStatus.active;

      this.eventTypeService
        .updateEventTypeStatus(eventTypeCopy)
        .then(updatedEventType => {
          this.successMessage = updatedEventType.name + ' activated.';
          this.loadEventTypes();
        })
        .catch(reason => {
          if (reason.status === 400) {
            this.errorMessage = 'Error activating event.';
            this.loadEventTypes();
          } else if (reason.status === 403) {
            this.errorMessage = 'You do not have permission to activate this event.';
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => { this.loadingSave = false; });
    };
  }

  public retireEventType(eventType: EventType) {
    this.successMessage = '';
    this.errorMessage = '';

    this.loadingSave = true;

    const retireConfirmedCallback = () => {
      this.loadingSave = true;

      const eventTypeCopy: EventType = {...eventType};
      eventTypeCopy.status = EventTypeStatus.retired;

      this.eventTypeService
        .updateEventTypeStatus(eventTypeCopy)
        .then(updatedEventType => {
          this.successMessage = updatedEventType.name + ' retired.';
          this.loadEventTypes();
        })
        .catch(reason => {
          if (reason.status === 400) {
            this.errorMessage = 'Error retiring event.';
            this.loadEventTypes();
          } else if (reason.status === 403) {
            this.errorMessage = 'You do not have permission to retire this event.';
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => {
          this.loadingSave = false;
        });
    };

    if (eventType.status === EventTypeStatus.draft) {
      const modalRef = this.modalService.open(SimpleMessageModalComponent);
      modalRef.componentInstance.headerText = 'Retire Event Type';
      modalRef.componentInstance.messageText = 'Are you sure you want to retire this event type? Once retired the event type may not be reverted to Active or Draft.';
      modalRef.componentInstance.messageAlertClass = 'alert-info';
      modalRef.componentInstance.hasConfirmation = true;
      modalRef.componentInstance.confirmedCallback = retireConfirmedCallback;
    } else if (eventType.schedule_type === ScheduleType.rolling) {
      const modalRef = this.modalService.open(SimpleMessageModalComponent);
      modalRef.componentInstance.headerText = 'Retire Event Type';
          modalRef.componentInstance.messageText = 'Are you sure you want to retire this event type? Once retired, all future events of this type will be removed, and the event type may not be reverted to Active.<br><br>This will <b>not</b> cancel any active roster memberships of this type. Please ensure that roster memberships are cancelled or scheduled to cancel as needed.';
      modalRef.componentInstance.messageAlertClass = 'alert-info';
      modalRef.componentInstance.hasConfirmation = true;
      modalRef.componentInstance.confirmedCallback = retireConfirmedCallback;
    } else {
      this.scheduledEventService
        .getFutureEventPartsWithReservations(eventType.id)
        .then(eventParts => {
          const modalRef = this.modalService.open(SimpleMessageModalComponent);
          modalRef.componentInstance.headerText = 'Retire Event Type';
          modalRef.componentInstance.messageText = 'Are you sure you want to retire this event type? Once retired, events of this type will no longer be available for booking, and the event type may not be reverted to Active. All future events of this type will be removed';
          if (eventParts.length > 0) {
            modalRef.componentInstance.messageText += ', except those with reservations.<br><br>The following instances have reservations, and will not be removed. You may instead cancel them manually via the scheduled events view.<ul class="mt-1 mb-0">';
            for (const eventPart of eventParts) {
              modalRef.componentInstance.messageText += (
                '<li>' +
                dateStringFromISO8601(eventPart.start_datetime) +
                ', ' +
                timeStringFromISO8601(eventPart.start_datetime) +
                ' - ' +
                timeStringFromISO8601(eventPart.end_datetime) +
                '</li>');
            }
            modalRef.componentInstance.messageText += '</ul>';
          } else {
            modalRef.componentInstance.messageText += '.';
          }
          modalRef.componentInstance.messageAlertClass = 'alert-info';
          modalRef.componentInstance.hasConfirmation = true;
          modalRef.componentInstance.confirmedCallback = retireConfirmedCallback;
        })
        .catch(reason => {
          if (reason.status === 400) {
            this.errorMessage = 'Error retiring event.';
            this.loadEventTypes();
          } else if (reason.status === 403) {
            this.errorMessage = 'You do not have permission to retire this event.';
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => {
          this.loadingSave = false;
        });
    }
  }

  public duplicateEventType(eventType: EventType) {
    this.successMessage = '';
    this.errorMessage = '';

    const modalRef = this.modalService.open(DuplicateEventTypeModalComponent);
    modalRef.componentInstance.eventType = eventType;
    modalRef.componentInstance.savedCallback = (newEventTypeId: number) => {
      this.router.navigate(
        [
          '/events/setup/edit/' + newEventTypeId.toString() + '/details'
        ]
      );
    }
  }

  public climberAppEventTypeDetailsUrl(eventType: EventType) {
    const baseUrl = this.orgService.getOrganizationConfig().climber_app_base_url;
    const etId = eventType.id.toString();
    return baseUrl + 'schedule/event-type-details/' + etId + '/';
  }

  /**
   * Handle a change in the filters.
   */
  public handleFilterChange() {
    this.pageNumber = 1;

    this.filtersChangedSubject.next();
  }
}
